<template lang="pug">
    #FolderPick.v-card
      v-card-title.d-flex.justify-center(style='color:#222222; font-weight: 700; font-size: 20px;')
        | {{ $t('FOLDER_LOCATION_PICKER.TITLE') || 'Select folder' }}
      v-card-text
        // Selected path info
        v-row.mt-2(no-gutters)
          v-col.d-flex.justify-center(cols="12")
            div(style="color:#444444; font-weight: 500; font-size: 14px;")
              | {{ $t('FOLDER_LOCATION_PICKER.CURRENT') || 'Current selection' }}:
            div.ml-1(style="color:#222222; font-weight: 700; font-size: 14px; word-break: break-all;")
              | {{ selectedLabel || ($t('FOLDER_LOCATION_PICKER.NOT_SELECTED') || '-') }}
        // TreeSelect
        v-row.mt-4(no-gutters)
          v-col(cols="12")
            div(style="border:1px solid #E6E6E6; border-radius: 8px; padding: 10px; min-height: 360px;")
              v-row.mt-2(v-if='loadingRoot' justify="center" align="center" class="fill-height")
                v-progress-circular(indeterminate size='40' color="#14C6DE" class="m-auto")
              div.mt-3(style="color:#777; font-size:12px;" v-if="loadingRoot")
                | {{ 'Loading...' }}
              treeselect(
                v-if="!loadingRoot"
                v-model="workingSelection"
                :options="treeOptions"
                :normalizer="normalizer"
                :disable-branch-nodes="false"
                :always-open="true"
                :default-expand-level="0"
                noResultsText="No data available"
                :matchKeys="match_keys"
                :disableFuzzyMatching="true"
                :multiple="false"
                :z-index="3000"
                :placeholder="$t('FOLDER_LOCATION_PICKER.PLACEHOLDER') || 'Select a folder...'"
                @input="onSelect"
              )
                template(v-slot:option-label="{ node }")
                  div.folder-node-row(:title="node.raw && node.raw.name")
                    template(v-if="node.raw && node.raw.type=='library'")
                      v-img.mr-1(src="@/assets/images/knowledge_select.svg" max-height="18" max-width="18")
                    template(v-else)
                      v-img.mr-1(src="@/assets/images/folder-btn.svg" max-height="18" max-width="18")
                    span.folder-label {{ (node.raw && node.raw.name) || node.label }}
                template(v-slot:value-label="{ node }")
                  span.folder-label(
                    v-if="node && node.raw"
                    :title="node.raw.name"
                  ) {{ node.raw.name }}
      // Footer
      div.d-flex.align-center.justify-end(style='background-color: #f4f4f4; height:60px;')
        v-btn.mr-4(
          :ripple="false"
          @click="onCancel"
          width="100"
          height="40"
          color="cancel"
          depressed
          outlined
          style='font-weight: 700;'
        ) {{ $t('GENERAL.CANCEL') }}
        v-btn.mr-6(
          :ripple="false"
          @click="onConfirm"
          width="100"
          height="40"
          color="#14C6DE"
          depressed
          :dark="!(loadingRoot || !workingSelection)"
          style='font-weight: 700;'
          :disabled="loadingRoot || !workingSelection"
        ) {{ $t('GENERAL.CONFIRM') }}
    </template>
    
    <script>
    import Vue from "vue";
    import Treeselect, { LOAD_CHILDREN_OPTIONS } from "@riophae/vue-treeselect";
    import "@riophae/vue-treeselect/dist/vue-treeselect.css";
    import UpGPTDataintegrationService from "@/services/UpGPTDataintegrationService";
    import KnowledgeLibraryService from "@/services/KnowledgeLibraryService";
    
    export default Vue.extend({
      name: "folderLocationPickerDialog",
      components: {
        Treeselect,
      },
      props: {
        value: {
          type: Boolean,
          default: false,
        },
        selectedId: {
          type: [String, Number, null],
          default: null,
        },
        selectedPath: {
          type: [String, Number, null],
          default: null,
        },
      },
      data() {
        return {
          loadingRoot: false,
          treeOptions: [],
          initialSelection: null,
          // store selected node id
          workingSelection: null,
          nodeMap: new Map(), // id -> normalized node
          match_keys: ["file_path", "id"],
        };
      },
      computed: {
        innerOpen: {
          get() {
            return this.value;
          },
          set(v) {
            this.$emit("input", v);
          },
        },
        selectedLabel() {
          if (this.workingSelection) {
            const n = this.nodeMap.get(this.workingSelection);
            if (n && n.path) return n.path;
          }
          return this.selectedPath || "";
        },
      },
      watch: {
        value: {
          immediate: true,
          async handler(open) {
            if (open) {
              this.initialSelection = this.selectedId || null;
              this.workingSelection = this.initialSelection;
    
              if (!this.treeOptions.length) {
                await this.loadRoot();
              }
            }
          },
        },
        selectedId: {
          immediate: true,
          handler(v) {
            if (!this.innerOpen) {
              this.initialSelection = v || null;
              this.workingSelection = v || null;
            }
          },
        },
      },
      methods: {
        filterFolderTree(nodes) {
          if (!Array.isArray(nodes)) return [];
          return nodes
            .filter(n => n && n.type === "folder")
            .map(n => {
              if (Array.isArray(n.children)) {
                n.children = this.filterFolderTree(n.children);
              }
              return n;
            });
        },
        applyFullPaths(nodes, parentPath) {
          if (!Array.isArray(nodes)) return;
          nodes.forEach(node => {
            if (!node) return;
            const name = (node.name ?? "").toString().replace(/^\/+/, "");
            const base = (parentPath ?? "").toString();
            const normalizedBase = base === "/" ? "/" : base.replace(/\/+$/, "");
            const currentPath = normalizedBase
              ? (normalizedBase === "/" ? `/${name}` : `${normalizedBase}/${name}`)
              : name;
            node.path = currentPath;
            if (Array.isArray(node.children) && node.children.length) {
              this.applyFullPaths(node.children, currentPath);
            }
          });
        },
        normalizer(raw) {
          const id = raw.id;
          const name = raw.name ?? raw.system_name ?? String(raw.id);
    
          const node = {
            id,
            label: name,
            name,
            // make Treeselect treat as "async children" when missing
            children: raw.children !== undefined ? raw.children : null,
            raw,
            path: raw.path,
          };
          this.nodeMap.set(id, node);
          return node;
        },
        async loadRoot() {
          this.loadingRoot = true;
          try {
            await KnowledgeLibraryService.getFolderTree().then(response => {
              const res_data = response.data?.tree || [];
              // reset cached nodes when reloading root
              this.nodeMap.clear();
              // Only keep folder (including all children)
              const folderOnly = this.filterFolderTree(res_data);

              // Compute full paths: Root/<child>/<grandchild>/...
              this.applyFullPaths(folderOnly, "/");

              // Wrap with a selectable Root node
              this.treeOptions = [
                {
                  id: "Root",
                  name: "/",
                  path: "/",
                  isDefaultExpanded: true,
                  children: folderOnly,
                },
              ];
            });
            // this.treeOptions = response.data?.my_drive || [];
          } finally {
            this.loadingRoot = false;
          }
        },
        async loadOptions({ action, parentNode, callback }) {
          if (action !== LOAD_CHILDREN_OPTIONS) return callback();
    
          try {
            parentNode.cloud_id = "";
            parentNode.cloud_type = "onedrive";
    
            const response = await UpGPTDataintegrationService.get_folder_files_list(parentNode);
            parentNode.children = response.data?.folder || [];
          } catch (e) {
            parentNode.children = [];
          } finally {
            callback();
          }
        },
        onSelect(val) {
          // val should be selected node id (because we removed valueFormat='object')
          this.workingSelection = val || null;
        },
        onCancel() {
          this.workingSelection = this.initialSelection;
          this.$emit("cancel");
          this.innerOpen = false;
        },
        onConfirm() {
          this.initialSelection = this.workingSelection;
          const s = this.nodeMap.get(this.workingSelection);
          if (!s) {
            this.$emit("confirm", { id: null, parent_folder_id: null, path: "" });
            this.innerOpen = false;
            return;
          }

          let data = {
            'id': s.id,
            'parent_folder': this.workingSelection,
            "path": s.path,
          }
          this.$emit("confirm", data);
          this.innerOpen = false;
        },
      },
    });
    </script>
    
    <style scoped>
    .folder-node-row {
      display: flex;
      align-items: center;
      min-height: 28px;
    }
    .folder-label {
      max-width: 420px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 500;
      color: #222;
    }
    </style>